/* eslint-disable sonarjs/no-duplicate-string */
import reset from 'styled-reset';
import { createGlobalStyle } from 'styled-components';
import * as PagesV2 from 'pagesv2';
import * as Pages from 'pages';
import { CreateIntercityTripV2 } from 'pages';
import * as Themes from 'styles/theme';
import { getLocalData } from 'hooks/useAuth';

const UI_VERSION_KEY = 'uiVersion';

// Roles allowed to toggle between old and new UI. All other roles are forced to 'new'.
const UI_TOGGLE_ALLOWED_ROLES = ['finance', 'control_tower', 'admin'];

export const getUIVersion = () => {
	const userType = getLocalData()?.user_type;
	if (userType && !UI_TOGGLE_ALLOWED_ROLES.includes(userType)) {
		if (localStorage.getItem(UI_VERSION_KEY) !== 'new') {
			localStorage.setItem(UI_VERSION_KEY, 'new');
		}
		return 'new';
	}
	return localStorage.getItem(UI_VERSION_KEY) || 'old';
};

export const GlobalStyle = createGlobalStyle`
	${reset}
	html {
		background-color: #F4F6F8;
		margin: 0;
		font-family: 'Roboto', sans-serif;
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
	}
	body { 
    margin: 0 auto;
	width: 100%;
	background-color: #F4F6F8;
	min-height: 100vh;
	}
`;

export const getThemes = (val) => {
	switch (val) {
		case 'oldtheme':
			return Themes.oldTheme;
		case 'orangetheme':
			return Themes.orangeTheme;
		default:
			return Themes.oldTheme;
	}
};

export const unAuthPages = [
	{ id: 1, key: '1', path: '/', element: <Pages.Login /> },
	{ id: 2, key: '2', path: '/login', element: <Pages.Login /> },
];

export const DOWNLOAD_STATUSES = [
	{ value: 'pending', label: 'Pending' },
	{ value: 'inprogress', label: 'In Progress' },
	{ value: 'processed', label: 'Processed' },
	{ value: 'failed', label: 'Failed' },
];

/**
 * In private page, we have included breadcrumds
 * to manage it in one place
 */

const employees = '/employees';

/**
 * Helper function to get the appropriate element based on UI version
 * @param {React.Component} oldElement - The old UI component
 * @param {React.Component} newElement - The new UI component (optional)
 * @returns {React.Component} - The appropriate element based on UI version
 */
const getElementByUIVersion = (oldElement, newElement = null) => {
	const uiVersion = getUIVersion();
	// If new UI is selected and newElement is provided, use it
	// Otherwise, fall back to old element
	return uiVersion === 'new' && newElement ? newElement : oldElement;
};

export const privatePages = [
	{
		path: '/dashboard',
		element: getElementByUIVersion(<Pages.Dashboard />),
		breadCrumbs: { id: 1, text: 'Dashboard', link: '' },
		module: 'dashboard',
	},
	{
		path: employees,
		element: getElementByUIVersion(<Pages.EmployeeManagementPage />),
		breadCrumbs: { id: 1, text: 'Employees List', link: '' },
		module: 'employee',
	},
	{
		path: '/employees/add',
		element: getElementByUIVersion(<Pages.EmployeeForm />),
		breadCrumbs: { id: 1, text: 'Create Employee', link: employees, hasArrow: true },
		module: 'employee',
	},
	{
		path: '/employees/:id',
		element: getElementByUIVersion(<Pages.EmployeeForm />),
		breadCrumbs: {
			id: 1,
			text: 'Edit Employee',
			link: employees,
			hasArrow: true,
			isGoBack: true,
		},
		module: 'employee',
	},
	{
		path: '/vehicles',
		element: getElementByUIVersion(
			<Pages.VehicleManagementPage />,
			<PagesV2.VehicleListPageV2 />
		),
		breadCrumbs: { id: 1, text: 'Vehicles', link: '' },
		module: 'vehicle',
	},
	{
		path: '/vehicles/add',
		element: getElementByUIVersion(<Pages.AddVehiclePage />, <PagesV2.CreateVehiclePageV2 />),
		breadCrumbs: { id: 1, text: 'Create Vehicles', link: '/vehicles', hasArrow: true },
		module: 'vehicle',
	},
	{
		path: '/vehicles/:id',
		element: getElementByUIVersion(<Pages.AddVehiclePage />, <PagesV2.VehicleDetailsPageV2 />),
		breadCrumbs: { id: 1, text: 'Vehicle Details', link: '/vehicles', hasArrow: true },
		module: 'vehicle',
	},
	{
		path: '/vehicle-type',
		element: getElementByUIVersion(null, <PagesV2.VehicleTypeListPageV2 />),
		breadCrumbs: { id: 1, text: 'Vehicle Type', link: '' },
		module: 'vehicle',
		v2Only: true,
	},
	{
		path: '/vehicle-type/add',
		element: getElementByUIVersion(null, <PagesV2.CreateVehicleTypePageV2 />),
		breadCrumbs: { id: 1, text: '', link: '/vehicle-type', hasArrow: false },
		module: 'vehicle',
		v2Only: true,
	},
	{
		path: '/vehicle-type/:id',
		element: getElementByUIVersion(null, <PagesV2.EditVehicleTypePageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Edit Vehicle Type',
			link: '/vehicle-type',
			hasArrow: true,
			isGoBack: true,
		},
		module: 'vehicle',
		v2Only: true,
	},
	{
		path: '/projects',
		element: getElementByUIVersion(<Pages.ProjectListPage />, <PagesV2.ProjectListPageV2 />),
		breadCrumbs: { id: 1, text: 'Project list', link: '' },
		module: 'project',
	},
	{
		path: '/projects/add',
		element: getElementByUIVersion(<Pages.AddProjectPage />, <PagesV2.CreateProjectPageV2 />),
		breadCrumbs: { id: 1, text: 'Create Project', link: '/projects', hasArrow: true },
		module: 'project',
	},
	{
		path: '/projects/:id',
		element: getElementByUIVersion(<Pages.AddProjectPage />, <PagesV2.CreateProjectPageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Edit Project',
			link: '/projects',
			hasArrow: true,
			isGoBack: true,
		},
		module: 'project',
	},
	{
		path: '/warehousing',
		element: getElementByUIVersion(null, <PagesV2.WarehousingDashboardPageV2 />),
		breadCrumbs: { id: 1, text: 'Warehousing', link: '' },
		module: 'warehouse',
		v2Only: true,
	},
	{
		path: '/warehousing/:projectId',
		element: getElementByUIVersion(null, <PagesV2.WarehouseProjectDetailPageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Warehouse Project',
			link: '/warehousing',
			hasArrow: true,
			isGoBack: true,
		},
		module: 'warehouse',
		v2Only: true,
	},
	{
		path: '/client-list',
		element: getElementByUIVersion(<Pages.ClientListPage />, <PagesV2.ClientListPageV2 />),
		breadCrumbs: { id: 1, text: 'Client List', link: '' },
		module: 'client',
	},
	{
		path: '/client-invoices',
		element: getElementByUIVersion(null, <PagesV2.ClientInvoiceListPageV2 />),
		breadCrumbs: { id: 1, text: 'Client Invoices', link: '' },
		module: 'client',
		v2Only: true,
	},
	{
		path: '/client-invoices/new',
		element: getElementByUIVersion(null, <PagesV2.CreateClientInvoicePageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Create Invoice',
			link: '/client-invoices',
			hasArrow: true,
		},
		module: 'client',
		v2Only: true,
	},
	{
		path: '/client-invoices/:id',
		element: getElementByUIVersion(null, <PagesV2.ClientInvoiceDetailPageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Invoice Details',
			link: '/client-invoices',
			hasArrow: true,
		},
		module: 'client',
		v2Only: true,
	},
	{
		path: '/client-invoices/:id/regenerate',
		element: getElementByUIVersion(null, <PagesV2.RegenerateClientInvoicePageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Regenerate Invoice',
			link: '/client-invoices',
			hasArrow: true,
		},
		module: 'client_invoice_regenerate',
		v2Only: true,
	},
	{
		path: '/client-invoices/:id/edit',
		element: getElementByUIVersion(null, <PagesV2.RegenerateClientInvoicePageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Edit Invoice',
			link: '/client-invoices',
			hasArrow: true,
		},
		module: 'client_invoice_regenerate',
		v2Only: true,
	},
	{
		path: '/clients',
		element: getElementByUIVersion(<Pages.ClientListPage />, <PagesV2.ClientListPageV2 />),
		breadCrumbs: { id: 1, text: 'Client List', link: '' },
		module: 'client',
	},
	{
		path: '/client/add',
		element: getElementByUIVersion(<Pages.AddClientPage />),
		breadCrumbs: { id: 1, text: 'Create Client', link: '/client-list', hasArrow: true },
		module: 'client',
	},
	{
		path: '/clients/:id',
		element: getElementByUIVersion(<Pages.AddClientPage />),
		breadCrumbs: {
			id: 1,
			text: 'Edit Client',
			link: '/client-list',
			hasArrow: true,
			isGoBack: false,
		},
		module: 'client',
	},
	{
		path: '/client/addsection',
		element: getElementByUIVersion(
			<Pages.CreateClientSection />,
			<PagesV2.CreateClientPageV2 />
		),
		breadCrumbs: { id: 1, text: 'Create Client', link: '/client-list', hasArrow: true },
		module: 'client',
	},
	{
		path: '/client/edit/:id',
		element: getElementByUIVersion(
			<Pages.CreateClientSection />,
			<PagesV2.CreateClientPageV2 />
		),
		breadCrumbs: {
			id: 1,
			text: 'Edit Client',
			link: '/client-list',
			hasArrow: true,
			isGoBack: false,
		},
		module: 'client',
	},
	{
		path: '/vendor/addsection',
		element: getElementByUIVersion(
			<Pages.CreateVendorSection />,
			<PagesV2.CreateVendorPageV2 />
		),
		breadCrumbs: { id: 1, text: 'Create Vendor', link: '/vendor-list', hasArrow: true },
		module: 'vendor',
	},
	{
		path: '/vendors/add',
		element: getElementByUIVersion(
			<Pages.CreateVendorSection />,
			<PagesV2.CreateVendorPageV2 />
		),
		breadCrumbs: { id: 1, text: 'Create Vendor', link: '/vendor-list', hasArrow: true },
		module: 'vendor',
	},
	{
		path: '/vendor/edit/:id',
		element: getElementByUIVersion(<Pages.CreateVendorSection />),
		breadCrumbs: {
			id: 1,
			text: 'Edit Vendor',
			link: '/vendor-list',
			hasArrow: true,
			isGoBack: false,
		},
		module: 'vendor',
	},
	{
		path: '/vendor-list',
		element: getElementByUIVersion(<Pages.VendorListPage />, <PagesV2.VendorListPageV2 />),
		breadCrumbs: { id: 1, text: 'Vendor List', link: '' },
		module: 'vendor',
	},
	{
		path: '/vendor/add',
		element: getElementByUIVersion(<Pages.CreateVendorForm />),
		breadCrumbs: { id: 1, text: 'Create Vendor', link: '/vendor-list', hasArrow: true },
		module: 'vendor',
	},
	{
		path: '/vendors/:id',
		element: getElementByUIVersion(<Pages.CreateVendorForm />, <PagesV2.EditVendorPageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Edit Vendor',
			link: '/vendor-list',
			hasArrow: true,
			isGoBack: true,
		},
		module: 'vendor',
	},
	{
		path: '/client-contracts',
		element: getElementByUIVersion(
			<Pages.ClientContractListPage />,
			<PagesV2.ClientContractListPageV2 />
		),
		breadCrumbs: { id: 1, text: 'Client Contracts', link: '' },
		module: 'client_contract',
	},
	{
		path: '/client-contracts/add',
		element: getElementByUIVersion(
			<Pages.CreateClientContractPage />,
			<PagesV2.CreateClientContractPageV2 />
		),
		breadCrumbs: { id: 1, text: 'Client Contracts', link: '/client-contracts', hasArrow: true },
		module: 'client_contract',
	},
	{
		path: '/client-contracts/:id',
		element: getElementByUIVersion(
			<Pages.CreateClientContractPage />,
			<PagesV2.CreateClientContractPageV2 />
		),
		breadCrumbs: {
			id: 1,
			text: 'Client Contracts',
			link: '/client-contracts',
			hasArrow: true,
			isGoBack: true,
		},
		module: 'client_contract',
	},
	{
		path: '/vendor-contracts',
		element: getElementByUIVersion(
			<Pages.VendorContractListPage />,
			<PagesV2.VendorContractListPageV2 />
		),
		breadCrumbs: { id: 1, text: 'Vendor Contract List', link: '' },
		module: 'vendor_contract',
	},
	{
		path: '/vendor-contracts/add',
		element: getElementByUIVersion(
			<Pages.CreateVendorContractPage />,
			<PagesV2.CreateVendorContractPageV2 />
		),
		breadCrumbs: { id: 1, text: 'Vendor Contracts', link: '/vendor-contracts', hasArrow: true },
		module: 'vendor_contract',
	},
	{
		path: '/vendor-contracts/:id',
		element: getElementByUIVersion(
			<Pages.CreateVendorContractPage />,
			<PagesV2.CreateVendorContractPageV2 />
		),
		breadCrumbs: {
			id: 1,
			text: 'Vendor Contracts',
			link: '/vendor-contracts',
			hasArrow: true,
			isGoBack: true,
		},
		module: 'vendor_contract',
	},

	{
		path: '/drivers',
		element: getElementByUIVersion(<Pages.DriversList />, <PagesV2.DriverListPageV2 />),
		breadCrumbs: { id: 1, text: 'Driver List', link: '' },
		module: 'driver',
	},
	{
		path: '/drivers/add',
		element: getElementByUIVersion(<Pages.CreateDriver />, <PagesV2.CreateDriverPageV2 />),
		breadCrumbs: { id: 1, text: 'Create Driver', link: '/drivers', hasArrow: true },
		module: 'driver',
	},
	{
		path: '/drivers/:id',
		element: getElementByUIVersion(<Pages.CreateDriver />, <PagesV2.EditDriverPageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'edit Driver',
			link: '/drivers',
			hasArrow: true,
			isGoBack: true,
		},
		module: 'driver',
	},
	// Route Management - V2 Only Feature
	{
		path: '/routes',
		element: getElementByUIVersion(null, <PagesV2.RouteListPageV2 />),
		breadCrumbs: { id: 1, text: 'Routes List', link: '' },
		module: 'route_management',
		v2Only: true,
	},
	{
		path: '/routes/create',
		element: getElementByUIVersion(null, <PagesV2.CreateRoutePageV2 />),
		breadCrumbs: {
			id: 1,
			text: '',
			link: '/routes',
			hasArrow: false,
			isGoBack: false,
		},
		module: 'route_management',
		v2Only: true,
	},
	{
		path: '/routes/:id',
		element: getElementByUIVersion(null, <PagesV2.CreateRoutePageV2 />),
		breadCrumbs: {
			id: 1,
			text: '',
			link: '/routes',
			hasArrow: false,
			isGoBack: false,
		},
		module: 'route_management',
		v2Only: true,
	},
	// Node Management - V2 Only Feature
	{
		path: '/nodes',
		element: getElementByUIVersion(null, <PagesV2.NodeListPageV2 />),
		breadCrumbs: { id: 1, text: 'Node Management', link: '' },
		module: 'node_management',
		v2Only: true,
	},
	{
		path: '/nodes/create',
		element: getElementByUIVersion(null, <PagesV2.CreateNodePageV2 />),
		breadCrumbs: {
			id: 1,
			text: '',
			link: '/nodes',
			hasArrow: false,
			isGoBack: false,
		},
		module: 'node_management',
		v2Only: true,
	},
	{
		path: '/nodes/:id',
		element: getElementByUIVersion(null, <PagesV2.NodeInformationPageV2 />),
		breadCrumbs: {
			id: 1,
			text: '',
			link: '/nodes',
			hasArrow: false,
			isGoBack: false,
		},
		module: 'node_management',
		v2Only: true,
	},
	{
		path: '/trip-history',
		element: getElementByUIVersion(<Pages.TripHistoryPage />),
		breadCrumbs: { id: 1, text: 'Trip History', link: '' },
		module: 'trip',
	},
	{
		path: '/trips/add',
		element: getElementByUIVersion(<Pages.CreateTrip />),
		breadCrumbs: { id: 1, text: 'Create Trip', link: '/trips', hasArrow: true },
		module: 'trip',
	},
	{
		path: '/trips/:id',
		element: getElementByUIVersion(<Pages.CreateTrip />),
		breadCrumbs: { id: 1, text: 'Edit Trip', link: '/trips', hasArrow: true, isGoBack: true },
		module: 'trip',
	},
	{
		path: '/trips/bulk-upload',
		element: getElementByUIVersion(<Pages.BulkUpload />),
		breadCrumbs: { id: 1, text: 'Bulk Trip', link: '/trip-history', hasArrow: true },
		module: 'trip',
	},
	{
		path: '/trips/intercity',
		element: getElementByUIVersion(<Pages.IntercityTripListPage />),
		breadCrumbs: { id: 1, text: 'Intercity Trip List', link: '' },
		module: 'trip',
	},
	{
		path: '/trips/intracity',
		element: getElementByUIVersion(<Pages.IntracityTripListPage />),
		breadCrumbs: { id: 1, text: 'Intracity Trip List', link: '' },
		module: 'trip',
	},
	{
		path: '/trips/:tripId/overview',
		element: getElementByUIVersion(<Pages.TripOverviewPage />, <Pages.TripOverviewPageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Trip Overview',
			link: '/trips/intercity',
			hasArrow: true,
			isGoBack: true,
		},
		module: 'trip',
	},
	{
		path: '/trips/add-intercity',
		element: getElementByUIVersion(<Pages.CreateIntercityTrip />, <CreateIntercityTripV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Create Intercity Trip',
			link: '/trips/intercity',
			hasArrow: true,
		},
		module: 'trip',
	},
	{
		path: '/trips/:id/add-intercity',
		element: getElementByUIVersion(<Pages.CreateIntercityTrip />, <CreateIntercityTripV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Edit Intercity Trip',
			link: '/trips/intercity',
			hasArrow: true,
		},
		module: 'trip',
	},
	{
		path: '/trips/create-lr',
		element: getElementByUIVersion(<Pages.CreateLR />),
		breadCrumbs: {
			id: 1,
			text: 'Create LR',
			link: '/trips/add-intercity',
			hasArrow: true,
			isGoBack: true,
			storeFormData: true,
		},
		module: 'trip',
	},
	{
		path: '/trips/:id/create-lr',
		element: getElementByUIVersion(<Pages.CreateLR />),
		breadCrumbs: {
			id: 1,
			text: 'Create LR',
			link: '/trips/intercity',
			hasArrow: true,
			isGoBack: true,
		},
		module: 'trip',
	},
	{
		path: '/trips/adhoc-request',
		element: getElementByUIVersion(<Pages.AdhocRequest />),
		breadCrumbs: {
			id: 1,
			text: 'Adhoc Request',
		},
		module: 'trip',
	},
	{
		path: '/trips/adhoc-request/create',
		element: getElementByUIVersion(<Pages.CreateAdhocRequest />),
		breadCrumbs: {
			id: 1,
			text: 'Create Adhoc Request',
			link: '/trips/adhoc-request',
			hasArrow: true,
		},
		module: 'trip',
	},
	{
		path: '/trips/adhoc-request/:id',
		element: getElementByUIVersion(<Pages.AdhocRequestDetails />),
		breadCrumbs: {
			id: 1,
			text: 'Request Details',
			link: '/trips/adhoc-request',
			hasArrow: true,
		},
		module: 'trip',
	},
	{
		path: '/vendor-invoices',
		element: getElementByUIVersion(<Pages.VendorInvoiceList />),
		breadCrumbs: { id: 1, text: 'Vendor Bill ', link: '' },
		module: 'vendor_bill',
	},
	{
		path: '/vendor-invoice-creation',
		element: getElementByUIVersion(<Pages.VendorBillGeneration />),
		breadCrumbs: {
			id: 1,
			text: 'Vendor Invoice Generation ',
			link: '/vendor-invoices',
			hasArrow: true,
		},
		module: 'vendor_bill',
	},
	{
		path: '/vendor-bill-invoice/:id',
		element: getElementByUIVersion(<Pages.VendorInvoicePage />),
		breadCrumbs: {
			id: 1,
			text: 'Vendor Invoice Generation ',
			link: '/vendor-invoices',
			hasArrow: true,
		},
		module: 'vendor_bill',
	},
	{
		path: '/vendor-bill-edting/:id',
		element: getElementByUIVersion(<Pages.VendorBillEditing />),
		breadCrumbs: {
			id: 1,
			text: 'Vendor Bill Editing ',
			link: '/vendor-invoices',
			hasArrow: true,
			isGoBack: true,
		},
		module: 'vendor_bill',
	},
	{
		path: '/vendor-bill-regeneration/:id',
		element: getElementByUIVersion(<Pages.VendorBillRegenration />),
		breadCrumbs: {
			id: 1,
			text: 'Vendor Bill Regeneration ',
			link: '/vendor-invoices',
			hasArrow: true,
			isGoBack: true,
		},
		module: 'vendor_bill_regenerate',
	},
	{
		path: '/airexpenses',
		element: getElementByUIVersion(<Pages.AirExpenseList />),
		breadCrumbs: {
			id: 1,
			text: 'Air Expense List ',
		},
		module: 'awb',
	},
	{
		path: '/airexpenses/air-bulk-upload',
		element: getElementByUIVersion(<Pages.AirUploadBulk />),
		breadCrumbs: {
			id: 1,
			text: 'Air Expenses Bulk Trip',
			link: '/airexpenses',
			hasArrow: true,
		},
		module: 'awb',
	},
	{
		path: '/approvals',
		element: getElementByUIVersion(<Pages.AllApprovals />, null),
		breadCrumbs: {
			id: 1,
			text: 'Approvals ',
		},
		module: 'approval_entities',
		oldUIOnly: true,
	},
	// {
	// 	path: '/approvals/ad-hoc-request',
	// 	element: getElementByUIVersion(
	// 		<Pages.AllApprovals />,
	// 		<PagesV2.AdHocRequestApprovalPageV2 />
	// 	),
	// 	breadCrumbs: (() => {
	// 		const uiVersion = getUIVersion();
	// 		return {
	// 			id: 1,
	// 			text: 'Ad-Hoc Request Approvals',
	// 			link: uiVersion === 'new' ? undefined : '/approvals', // No link in new UI
	// 			hasArrow: uiVersion === 'old', // Only show arrow in old UI
	// 		};
	// 	})(),
	// 	module: 'approval_entities',
	// },
	// {
	// 	path: '/approvals/ad-hoc-request/:id',
	// 	element: getElementByUIVersion(<Pages.AdhocRequestDetails />),
	// 	breadCrumbs: {
	// 		id: 1,
	// 		text: 'Request Details',
	// 		link: '/approvals/ad-hoc-request',
	// 		hasArrow: true,
	// 	},
	// 	module: 'approval_entities',
	// },
	{
		path: '/approvals/client-contract',
		element: getElementByUIVersion(
			<Pages.AllApprovals />,
			<PagesV2.ClientContractApprovalPageV2 />
		),
		breadCrumbs: (() => {
			const uiVersion = getUIVersion();
			return {
				id: 1,
				text: 'Client Contract Approvals',
				link: uiVersion === 'new' ? undefined : '/approvals', // No link in new UI
				hasArrow: uiVersion === 'old', // Only show arrow in old UI
			};
		})(),
		module: 'approval_entities',
	},
	{
		path: '/approvals/client-contract/:id',
		element: getElementByUIVersion(<PagesV2.ClientContractDetailsPageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Contract Details',
			link: '/approvals/client-contract',
			hasArrow: true,
		},
		module: 'approval_entities',
	},
	{
		path: '/approvals/client-invoice-edit-approval',
		element: getElementByUIVersion(null, <PagesV2.ClientInvoiceRegenApprovalPageV2 />),
		breadCrumbs: (() => {
			const uiVersion = getUIVersion();
			return {
				id: 1,
				text: 'Client Invoice Edit Approval',
				link: uiVersion === 'new' ? undefined : '/approvals',
				hasArrow: uiVersion === 'old',
			};
		})(),
		module: 'approval_entities',
		v2Only: true,
	},
	{
		path: '/approvals/client-invoice-edit-approval/:requestId',
		element: getElementByUIVersion(null, <PagesV2.ClientInvoiceRegenApprovalDetailsPageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Regeneration Request',
			link: '/approvals/client-invoice-edit-approval',
			hasArrow: true,
		},
		module: 'approval_entities',
		v2Only: true,
	},
	{
		path: '/approvals/vendor-contract',
		element: getElementByUIVersion(
			<Pages.AllApprovals />,
			<PagesV2.VendorContractApprovalPageV2 />
		),
		breadCrumbs: (() => {
			const uiVersion = getUIVersion();
			return {
				id: 1,
				text: 'Vendor Contract Approvals',
				link: uiVersion === 'new' ? undefined : '/approvals', // No link in new UI
				hasArrow: uiVersion === 'old', // Only show arrow in old UI
			};
		})(),
		module: 'approval_entities',
	},
	{
		path: '/approvals/vendor-contract/:id',
		element: getElementByUIVersion(<PagesV2.VendorContractDetailsPageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Contract Details',
			link: '/approvals/vendor-contract',
			hasArrow: true,
		},
		module: 'approval_entities',
	},
	{
		path: '/approvals/vendor-bank-details',
		element: getElementByUIVersion(
			<Pages.AllApprovals />,
			<PagesV2.VendorBankDetailsApprovalPageV2 />
		),
		breadCrumbs: (() => {
			const uiVersion = getUIVersion();
			return {
				id: 1,
				text: 'Vendor Bank Details Approvals',
				link: uiVersion === 'new' ? undefined : '/approvals',
				hasArrow: uiVersion === 'old',
			};
		})(),
		module: 'approval_entities',
	},
	{
		path: '/approvals/vendor-bank-details/:id',
		element: getElementByUIVersion(<PagesV2.VendorBankDetailsDetailsPageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Bank Details',
			link: '/approvals/vendor-bank-details',
			hasArrow: true,
		},
		module: 'approval_entities',
	},
	{
		path: '/approvals/driver',
		element: getElementByUIVersion(null, <PagesV2.VerificationApprovalPageV2 kind="driver" />),
		breadCrumbs: (() => {
			const uiVersion = getUIVersion();
			return {
				id: 1,
				text: 'Driver Approvals',
				link: uiVersion === 'new' ? undefined : '/approvals',
				hasArrow: uiVersion === 'old',
			};
		})(),
		module: 'approval_entities',
		v2Only: true,
	},
	{
		path: '/approvals/driver/:id',
		element: getElementByUIVersion(null, <PagesV2.VerificationDetailsPageV2 kind="driver" />),
		breadCrumbs: {
			id: 1,
			text: 'Driver Documents',
			link: '/approvals/driver',
			hasArrow: true,
		},
		module: 'approval_entities',
		v2Only: true,
	},
	{
		path: '/approvals/vehicle',
		element: getElementByUIVersion(null, <PagesV2.VerificationApprovalPageV2 kind="vehicle" />),
		breadCrumbs: (() => {
			const uiVersion = getUIVersion();
			return {
				id: 1,
				text: 'Vehicle Approvals',
				link: uiVersion === 'new' ? undefined : '/approvals',
				hasArrow: uiVersion === 'old',
			};
		})(),
		module: 'approval_entities',
		v2Only: true,
	},
	{
		path: '/approvals/vehicle/:id',
		element: getElementByUIVersion(null, <PagesV2.VerificationDetailsPageV2 kind="vehicle" />),
		breadCrumbs: {
			id: 1,
			text: 'Vehicle Documents',
			link: '/approvals/vehicle',
			hasArrow: true,
		},
		module: 'approval_entities',
		v2Only: true,
	},
	{
		path: '/approvals/vehicle-replacement',
		element: getElementByUIVersion(
			<Pages.AllApprovals />,
			<PagesV2.VehicleReplacementApprovalPageV2 />
		),
		breadCrumbs: (() => {
			const uiVersion = getUIVersion();
			return {
				id: 1,
				text: 'Vehicle Replacement Approvals',
				link: uiVersion === 'new' ? undefined : '/approvals',
				hasArrow: uiVersion === 'old',
			};
		})(),
		module: 'approval_entities',
	},
	{
		path: '/approvals/vehicle-replacement/:id',
		element: getElementByUIVersion(<PagesV2.VehicleReplacementDetailsPageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Vehicle Replacement',
			link: '/approvals/vehicle-replacement',
			hasArrow: true,
		},
		module: 'approval_entities',
	},
	{
		path: '/approvals/trip-pod-approvals',
		element: getElementByUIVersion(null, <PagesV2.TripAndPODApprovalPageV2 />),
		breadCrumbs: (() => {
			const uiVersion = getUIVersion();
			return {
				id: 1,
				text: 'Trip and POD Approvals',
				link: uiVersion === 'new' ? undefined : '/approvals',
				hasArrow: uiVersion === 'old',
			};
		})(),
		module: 'approval_entities',
	},
	{
		path: '/approvals/invoice-edit-approval',
		element: getElementByUIVersion(
			<Pages.AllApprovals />,
			<PagesV2.InvoiceEditApprovalPageV2 />
		),
		breadCrumbs: (() => {
			const uiVersion = getUIVersion();
			return {
				id: 1,
				text: 'Invoice Edit Approvals',
				link: uiVersion === 'new' ? undefined : '/approvals',
				hasArrow: uiVersion === 'old',
			};
		})(),
		module: 'approval_entities',
	},
	{
		path: '/approvals/invoice-edit-approval/:id',
		element: getElementByUIVersion(<PagesV2.InvoiceEditApprovalDetailsPageV2 />),
		breadCrumbs: {
			id: 1,
			text: 'Invoice Edit Details',
			link: '/approvals/invoice-edit-approval',
			hasArrow: true,
		},
		module: 'approval_entities',
	},
	{
		path: '/downloads',
		element: getElementByUIVersion(<Pages.DownloadReports />),
		breadCrumbs: {
			id: 1,
			text: 'Download Reports ',
		},
		module: 'download_task',
	},
	{
		path: '/lr',
		element: getElementByUIVersion(<Pages.LRListPage />),
		breadCrumbs: {
			id: 1,
			text: 'LR Management ',
		},
		module: 'trip',
	},
	// {
	// 	path: '/lr/add',
	// 	element: getElementByUIVersion(<Pages.LRCreationForm />),
	// 	breadCrumbs: {
	// 		id: 1,
	// 		text: 'LR Management',
	// 	},
	// 	module: 'trip',
	// },
	// {
	// 	path: '/lr/:id',
	// 	element: getElementByUIVersion(<Pages.LRCreationForm />),
	// 	breadCrumbs: {
	// 		id: 1,
	// 		text: 'LR Manager',
	// 		link: '/lr',
	// 		hasArrow: true,
	// 		isGoBack: true,
	// 	},
	// 	module: 'trip',
	// },
	{
		path: '/temperature-dashboard',
		element: getElementByUIVersion(<Pages.TemperatureDashboard />),
		breadCrumbs: {
			id: 1,
			text: 'Temperature Dashboard',
		},
		module: 'temperature_dashboard',
	},
	{
		path: '/trip/add-intracity',
		element: getElementByUIVersion(<Pages.CreateIntracityTrip />),
		breadCrumbs: {
			id: 1,
			text: 'Create Intracity Trip',
			link: '/trips/intracity',
			hasArrow: true,
		},
		module: 'trip',
	},
	{
		path: '/trips/:id/add-intracity',
		element: getElementByUIVersion(<Pages.CreateIntracityTrip />),
		breadCrumbs: {
			id: 1,
			text: 'Edit Intracity Trip',
			link: '/trips/intracity',
			hasArrow: true,
		},
		module: 'trip',
	},
	{
		path: '/control-tower/whatsapp',
		element: getElementByUIVersion(<Pages.WhatsApp />),
		breadCrumbs: {
			id: 1,
			text: 'WhatsApp',
		},
		module: 'control_tower',
	},
	{
		path: '/business-units',
		element: getElementByUIVersion(<Pages.BusinessUnitPage />),
		breadCrumbs: { id: 1, text: 'Business Units', link: '' },
		module: 'business_unit',
	},
	{
		path: '/business-units/:id',
		element: getElementByUIVersion(<Pages.BusinessUnitView />),
		breadCrumbs: { id: 2, text: 'Business Unit -', link: '/businessUnit', hasArrow: true },
		module: 'business_unit',
	},
	{
		path: '/finance/requests',
		element: getElementByUIVersion(<Pages.FinanceRequestsPage />),
		breadCrumbs: { id: 1, text: 'Finance Requests', link: '' },
		module: 'finance',
	},
	{
		path: '/finance/requests/:id',
		element: getElementByUIVersion(
			<Pages.FinanceRequestDetails />,
			<Pages.FinanceRequestDetails />
		),
		breadCrumbs: {
			id: 1,
			text: 'Finance Request Details',
			hasArrow: true,
			isGoBack: true,
		},
		module: 'finance',
	},
	{
		path: '/finance/payments',
		element: getElementByUIVersion(<Pages.FinancePaymentsPage />),
		breadCrumbs: { id: 1, text: 'Finance Payments', link: '' },
		module: 'finance',
	},
];

// to do : find all the pages where we have to change the Ui and revamp it
// make a new file for the page that needs to be revamped and in the element add the new page component
